// Pure title-summary logic; SDK-free and testable (name lookup is injected).

// Field types that can nest blocks
export const NESTING_FIELD_TYPES = ['rich_text', 'structured_text', 'single_block'];
export const MAX_NAMES_SHOWN = 3;

// Structural subset of an SDK Field, so tests need not import the SDK
export type NestingField = {
  attributes: {
    field_type: string;
    api_key: string;
    localized?: boolean;
  };
};

// Block type id from form shape ({ itemTypeId }) or CMA shape
// ({ relationships.item_type.data.id }). Bare-string ids resolve to null.
export function childItemTypeId(entry: any): string | null {
  if (!entry || typeof entry !== 'object') return null;
  if (typeof entry.itemTypeId === 'string') return entry.itemTypeId;
  const relId = entry.relationships?.item_type?.data?.id;
  if (typeof relId === 'string') return relId;
  const attrId = entry.attributes?.itemTypeId;
  if (typeof attrId === 'string') return attrId;
  return null;
}

// Collect direct block/inlineBlock items from a structured-text tree.
// Does not descend into an item, so grandchildren aren't double-counted.
export function collectStructuredTextBlockItems(node: any, out: any[]): void {
  if (Array.isArray(node)) {
    node.forEach((n) => collectStructuredTextBlockItems(n, out));
    return;
  }
  if (!node || typeof node !== 'object') return;
  if ((node.type === 'block' || node.type === 'inlineBlock') && node.item != null) {
    out.push(node.item);
    return;
  }
  Object.values(node).forEach((v) => collectStructuredTextBlockItems(v, out));
}

// Summary of a block's direct children, e.g. "FAQ Item ×4, CTA, +2 more",
// or null when there are no nesting fields / no children.
export function summarizeNestedBlocks(
  fields: NestingField[],
  attributes: Record<string, any>,
  resolveName: (itemTypeId: string) => string | null,
): string | null {
  const nestingFields = fields.filter((f) => NESTING_FIELD_TYPES.includes(f.attributes.field_type));
  if (nestingFields.length === 0) return null;

  const nameCounts = new Map<string, number>();
  let unnamed = 0;

  const addChild = (entry: any) => {
    const id = typeof entry === 'string' ? null : childItemTypeId(entry);
    const name = id ? resolveName(id) : null;
    if (name) {
      nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
    } else {
      unnamed++;
    }
  };

  for (const field of nestingFields) {
    const raw = attributes[field.attributes.api_key];
    if (raw == null) continue;

    // Localized fields wrap the value in a { locale: value } object
    const values =
      field.attributes.localized && typeof raw === 'object' && !Array.isArray(raw) && !childItemTypeId(raw)
        ? Object.values(raw)
        : [raw];

    for (const value of values) {
      if (value == null) continue;
      switch (field.attributes.field_type) {
        case 'rich_text':
          if (Array.isArray(value)) value.forEach(addChild);
          break;
        case 'single_block':
          addChild(value);
          break;
        case 'structured_text': {
          const blockItems: any[] = [];
          collectStructuredTextBlockItems(value, blockItems);
          blockItems.forEach(addChild);
          break;
        }
      }
    }
  }

  const namedTotal = Array.from(nameCounts.values()).reduce((a, b) => a + b, 0);
  const total = namedTotal + unnamed;
  if (total === 0) return null;
  if (nameCounts.size === 0) return `${total} nested block${total > 1 ? 's' : ''}`;

  const sorted = Array.from(nameCounts.entries()).sort((a, b) => b[1] - a[1]);
  const shown = sorted
    .slice(0, MAX_NAMES_SHOWN)
    .map(([name, count]) => (count > 1 ? `${name} ×${count}` : name));
  const hiddenCount = sorted.slice(MAX_NAMES_SHOWN).reduce((sum, [, count]) => sum + count, 0) + unnamed;
  if (hiddenCount > 0) shown.push(`+${hiddenCount} more`);
  return shown.join(', ');
}
